import { Box, Button, Stack, Text } from '@chakra-ui/react'
import type { CaptureTokenResponse, RegistrationStatus } from '@ipc/shared'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { NS } from '~/i18n'
import { useOpenFaq } from '~/components/member/WhyLink'
import { browserCamera, CameraError, type Camera, type CameraSession } from './camera'
import { TRIGGERS, TriggerError, type Trigger } from './triggers'

/**
 * The server holds an upload back until this long after the token (CAPTURE_MIN_MS, 1,000 ms by default); the page
 * waits a little longer than that so a slow clock never makes a good capture look too fast (AC2).
 */
export const CAPTURE_MIN_MS = 1100
const POLL_MS = 100

type Phase =
  | 'opening'
  | 'find'
  | 'hold'
  | 'sending'
  | 'done'
  | 'camera_error'
  | 'retry_quality'
  | 'retry_again'
  | 'retry_motion'
  | 'not_started'
  /** 409 `manual_route`: no face reference to compare with; a person verifies by video call (no "Try again"). */
  | 'manual_route'

export interface CaptureScreenProps {
  providerId: string
  /** The camera seam (tests pass a fake one). */
  camera?: Camera
  requestToken: () => Promise<CaptureTokenResponse>
  /** Uploads exactly `profile.frames` pictures with the token (multipart). */
  upload: (token: string, frames: Blob[]) => Promise<RegistrationStatus>
  onDone: (registration: RegistrationStatus) => void
  /** The trigger registry (tests pass their own; the default is this build's). */
  triggers?: Record<string, Trigger>
  /** T23: the line above the camera (default "Look at the camera"), e.g. the post-liveness still's line. */
  title?: string
  /** T23: the text for a refusal reason (e.g. `still_not_matched`), shown with "Try again" (not a try). */
  refusalText?: Record<string, string>
}

const sleep = (ms: number, signal: AbortSignal) =>
  new Promise<void>((resolve, reject) => {
    if (signal.aborted) return reject(new TriggerError('aborted'))
    const id = setTimeout(resolve, ms)
    signal.addEventListener(
      'abort',
      () => {
        clearTimeout(id)
        reject(new TriggerError('aborted'))
      },
      { once: true }
    )
  })

const refusalReason = (err: unknown): string | null => {
  const e = err as { code?: string; details?: { reason?: string } } | null
  return e?.code === 'capture_refused' ? (e.details?.reason ?? 'unknown') : null
}

/**
 * The shared camera screen (copy v8 "S4 own camera screen"; spec E8.2; AC2). It opens the camera, shows the oval,
 * asks the server for a token when the face is in place, runs the trigger the server's frame plan names, holds the
 * upload until CAPTURE_MIN_MS after the token, and uploads once. Nothing about a provider is decided here.
 * No picture is kept: frames live in memory until the upload, the canvas is cleared, the tracks are stopped.
 */
export function CaptureScreen({
  providerId,
  camera = browserCamera,
  requestToken,
  upload,
  onDone,
  triggers = TRIGGERS,
  title,
  refusalText,
}: CaptureScreenProps) {
  const { t } = useTranslation(NS)
  const openFaq = useOpenFaq()
  const videoRef = useRef<HTMLVideoElement>(null)
  const [phase, setPhase] = useState<Phase>('opening')
  const [prompt, setPrompt] = useState<string | null>(null)
  const [motionTips, setMotionTips] = useState(false)
  const [run, setRun] = useState(0)
  const [retryText, setRetryText] = useState<string | null>(null)
  const latest = useRef({ requestToken, upload, onDone, triggers, camera, t, refusalText })
  latest.current = { requestToken, upload, onDone, triggers, camera, t, refusalText }

  useEffect(() => {
    const ac = new AbortController()
    const { signal } = ac
    let session: CameraSession | null = null
    const fail = (p: Phase) => {
      if (!signal.aborted) setPhase(p)
    }

    void (async () => {
      setPhase('opening')
      setRetryText(null)
      setPrompt(null)
      setMotionTips(false)
      try {
        const video = videoRef.current
        if (!video) return
        session = await latest.current.camera.open(video)
        if (signal.aborted) return
        setPhase('find')
        while (!(await session.faceInOval())) await sleep(POLL_MS, signal)

        setPhase('hold')
        const tok = await latest.current.requestToken()
        const tokenAt = Date.now()
        const { profile } = tok
        const trigger = latest.current.triggers[profile.secondFrame]
        if (!trigger) return fail('not_started')
        setMotionTips(profile.secondFrame === 'motion')
        const open = session
        const frames = await trigger({
          video: open.video,
          grab: () => open.grab({ encoding: profile.encoding, mirror: profile.mirror }),
          profile,
          challenge: tok.challenge,
          t: (k, v) => latest.current.t(k, v ?? {}),
          signal,
          say: (text) => !signal.aborted && setPrompt(text),
        })
        setPrompt(null)
        await sleep(Math.max(0, CAPTURE_MIN_MS - (Date.now() - tokenAt)), signal)

        setPhase('sending')
        // The camera is not needed any more: release it before the upload.
        session.stop()
        session = null
        const result = await latest.current.upload(tok.captureToken, frames)
        if (signal.aborted) return
        setPhase('done')
        latest.current.onDone(result)
      } catch (err) {
        if (signal.aborted) return
        if (err instanceof TriggerError) return fail(err.code === 'no_motion' ? 'retry_motion' : 'not_started')
        if (err instanceof CameraError) return fail('camera_error')
        if ((err as { code?: string } | null)?.code === 'manual_route') return fail('manual_route')
        const reason = refusalReason(err)
        if (reason === 'quality' || reason === 'consistency') return fail('retry_quality')
        const custom = reason ? latest.current.refusalText?.[reason] : undefined
        if (custom) {
          setRetryText(custom)
          return fail('retry_again')
        }
        if (reason) return fail('retry_again')
        return fail('not_started')
      } finally {
        session?.stop()
      }
    })()

    return () => {
      ac.abort()
      session?.stop()
    }
  }, [run])

  const again = useCallback(() => setRun((n) => n + 1), [])
  const failed =
    phase.startsWith('retry_') || phase === 'camera_error' || phase === 'not_started' || phase === 'manual_route'

  const status =
    phase === 'opening' || phase === 'find'
      ? t('capture.putInOval')
      : phase === 'hold'
        ? t('capture.stayStill')
        : phase === 'sending'
          ? t('capture.sending')
          : phase === 'done'
            ? t('capture.done')
            : ''

  return (
    <Stack gap={5} data-provider={providerId}>
      <Text fontWeight='bold'>{title ?? t('capture.look')}</Text>
      <Box
        position='relative'
        w='full'
        maxW='420px'
        mx='auto'
        aspectRatio='3 / 4'
        display={failed ? 'none' : 'block'}
        bg='bg.muted'
      >
        <video
          ref={videoRef}
          muted
          playsInline
          aria-label={t('capture.videoLabel')}
          style={{ width: '100%', height: '100%', objectFit: 'cover', transform: 'scaleX(-1)' }}
        />
        <Box
          aria-hidden
          position='absolute'
          top='15%'
          left='25%'
          w='50%'
          h='70%'
          borderRadius='50%'
          borderWidth='4px'
          borderStyle='solid'
          borderColor={phase === 'hold' || phase === 'sending' || phase === 'done' ? 'green.500' : 'white'}
          boxShadow='0 0 0 9999px rgba(0,0,0,0.35)'
          pointerEvents='none'
        />
      </Box>
      <Box aria-live='polite' minH='12'>
        {status && <Text fontSize='lg'>{status}</Text>}
        {prompt && <Text fontSize='lg'>{prompt}</Text>}
        {phase === 'hold' && motionTips && <Text>{t('capture.moveIntro')}</Text>}
      </Box>
      {motionTips && !failed && (
        <Stack gap={1} as='ul' ps={5}>
          <li>{t('tips.still')}</li>
          <li>{t('tips.steady')}</li>
        </Stack>
      )}
      {failed && (
        // Buttons full width on a phone (mobile review: "Show me how to allow the camera" touched the edge at 320 px).
        <Stack gap={3} align={{ base: 'stretch', sm: 'flex-start' }} role='alert'>
          {phase === 'camera_error' && (
            <>
              <Text fontWeight='bold'>{t('capture.noCamera')}</Text>
              <Button onClick={again}>{t('common.tryAgain')}</Button>
              <Text>{t('capture.noCameraBlock')}</Text>
              <Button variant='outline' onClick={() => openFaq('cameraAllow')}>
                {t('capture.noCameraHow')}
              </Button>
              <Text>{t('capture.noCameraOther')}</Text>
            </>
          )}
          {phase === 'retry_quality' && <Text>{t('capture.poorQuality')}</Text>}
          {phase === 'retry_again' && <Text>{retryText ?? t('capture.again')}</Text>}
          {phase === 'retry_motion' && <Text>{t('capture.noMotion')}</Text>}
          {phase === 'not_started' && <Text>{t('outcome.notStarted')}</Text>}
          {phase === 'manual_route' && <Text>{t('errors.manual_route')}</Text>}
          {phase !== 'camera_error' && phase !== 'manual_route' && (
            <Button onClick={again}>{t('common.tryAgain')}</Button>
          )}
        </Stack>
      )}
    </Stack>
  )
}
